<template>
  <div class="flex flex-col min-h-0 min-w-0 w-full h-full">
    <div
      class="border-b border-mid-neutral-50 h-14 flex items-center justify-between px-6 bg-white z-10 shrink-0"
    >
      <div class="flex items-center gap-4 text-dark-neutral-500">
        <RouterLink
          to="/compliance/microbiology/sterilization/"
          class="flex items-center gap-1 text-sm font-medium text-dark-neutral-400 hover:text-dark-neutral-600 focus:outline-none"
        >
          <span class="material-symbols-outlined text-base">arrow_back</span>
          <span>Back</span>
        </RouterLink>
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined">microwave</span>
          <h2 class="font-semibold">Sterilization</h2>
        </div>
      </div>
    </div>
    <div class="p-6">
      <div v-if="sterilizationSpecMetadata" class="space-y-6">
        <div>
          <h3
            class="text-xs font-medium text-dark-neutral-300 uppercase tracking-wide"
          >
            Phase
          </h3>
          <div class="text-lg font-semibold">
            {{ sterilizationSpecMetadata.phase.name }}
          </div>
        </div>
        <div>
          <h3
            class="text-xs font-medium text-dark-neutral-300 uppercase tracking-wide mb-2"
          >
            Variants
          </h3>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="v in allVariants"
              :key="v.id"
              class="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white"
            >
              <div
                class="w-5 h-5 rounded-md"
                :style="{
                  backgroundColor: lightenColor(v.color),
                  border: '2px solid ' + v.color,
                }"
              ></div>
              <span class="font-medium">{{ v.name }}</span>
              <span
                v-if="v.id === sterilizationSpecMetadata.mainVariant.id"
                class="text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded bg-dark-neutral-500 text-white"
                >Main</span
              >
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-sm text-dark-neutral-300">
        No specification found.
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { lifecyclePhasesState } from "states/lifecyclePhases";
import { useSterilizationSpecificationsState } from "states/sterilization";
import { variantsState } from "states/variants";
import type { SpecificationMetadata, SpecificationMetadataState } from "types";
import { computed } from "vue";
import { lightenColor } from "utils";
import { useRoute } from "vue-router";

const route = useRoute();
const sterilizationSpecs = useSterilizationSpecificationsState();

const variants = variantsState();
const { phases } = lifecyclePhasesState();
const sterilizationSpecMetadata = computed(() => {
  const specState = sterilizationSpecs.value.find(
    (spec) => spec.id === route.params.id
  );

  if (!specState) return null;

  return fromState(specState);
});

const allVariants = computed(() => {
  if (!sterilizationSpecMetadata.value) return [];
  const main = sterilizationSpecMetadata.value.mainVariant;
  const others = sterilizationSpecMetadata.value.variants.filter(
    (v) => v.id !== main.id
  );
  return [main, ...others];
});

function fromState(
  state: SpecificationMetadataState
): SpecificationMetadata | null {
  const variant = variants.value.find((v) => v.id === state.mainVariantId);
  const phase = phases.value.find((p) => p.id === state.phaseId);
  if (!variant || !phase) return null;
  return {
    id: state.id,
    mainVariant: variant,
    phase,
    variants:
      state.variantIds
        ?.map((id) => variants.value.find((v) => v.id === id))
        .filter((v) => v !== undefined) ?? [],
  };
}
</script>
